/*
  Zorali: storefront tweaks for the MagicX "AI Autocomplete" app.
  Loaded from layout/theme.liquid (kept under the original asset name so the include never changes).

  1. Option and chip icons off: embed config, SDK options and a CSS fallback inside the widget's shadow root.
  2. "AI Autocomplete" badge removed from the dropdown footer (the "tab to select" hint stays).
  3. Dropdown no longer opens on page load: the SDK's autoFocus is switched off, so it opens on focus/typing.
  4. Spare-part products (Replacement ..., $1 parts, GWPs) hidden from the Products row, and the
     "Replacements" product-type option hidden from the options grid. The app's catalog sync includes
     products that are not published to the Online Store, and their cards 404 when clicked.
  5. The theme's rotating "Search for ..." label (header.liquid, setting collection_names) shows over the
     widget while it is empty and unfocused. Companion CSS lives in assets/custom.css (.zorali-ai-idle).
*/
(() => {
  'use strict';

  const HOST_SELECTOR = '.ai-search-bar__sdk';
  const FRAME_SELECTOR = '.ai-search-embed-frame';
  const SDK_GLOBAL = 'MagicxAIAutocomplete';
  const STYLE_ID = 'zorali-ai-search-tweaks';
  const IDLE_CLASS = 'zorali-ai-idle';
  const HIDDEN_ATTR = 'data-zorali-hidden';

  // Products whose handle matches, or whose price is below this, are spare parts or giveaways.
  const EXCLUDE_HANDLE = /(replacement|spare|repair|gwp|(^|-)parts?(-|$))/i;
  const MIN_PRICE = 5;
  const EXCLUDE_OPTION = /^replacements?$/i;

  const SDK_OVERRIDES = { autoFocus: false, showOptionIcons: false, showChipIcons: false };

  // ---- 1 + 3: embed config and SDK constructor overrides ---------------------------------
  const embedConfig = window.__AI_SEARCH_EMBED_CONFIG__;
  if (embedConfig) {
    embedConfig.showOptionIcons = false;
    embedConfig.showChipIcons = false;
  }

  function patchSdk(sdk) {
    if (!sdk || typeof sdk !== 'object' || typeof sdk.AIAutocomplete !== 'function') return sdk;
    if (sdk.__zoraliPatched) return sdk;
    const Original = sdk.AIAutocomplete;
    class ZoraliAIAutocomplete extends Original {
      constructor(container, options) {
        super(container, Object.assign({}, options || {}, SDK_OVERRIDES));
      }
    }
    // The SDK's export object is getter-only, so hand back a proxy instead of reassigning.
    return new Proxy(sdk, {
      get(target, key) {
        if (key === 'AIAutocomplete') return ZoraliAIAutocomplete;
        if (key === '__zoraliPatched') return true;
        return target[key];
      }
    });
  }

  const descriptor = Object.getOwnPropertyDescriptor(window, SDK_GLOBAL);
  if (descriptor && 'value' in descriptor) {
    // SDK already loaded (script order changed). Patch in place; the embed reads the global when it runs.
    if (descriptor.writable) window[SDK_GLOBAL] = patchSdk(descriptor.value);
  } else if (!descriptor) {
    // Normal case: this script runs before the SDK. Its `var MagicxAIAutocomplete = ...` lands on this setter.
    let stored;
    Object.defineProperty(window, SDK_GLOBAL, {
      configurable: true,
      enumerable: true,
      get() { return stored; },
      set(value) { stored = patchSdk(value); }
    });
  }

  // Fallback for instances that were created before this script ran.
  function fixExistingInstances() {
    const handles = window.__aiSearchEmbed;
    if (!Array.isArray(handles)) return;
    for (const handle of handles) {
      const ac = handle && handle.ac;
      if (!ac || ac.__zoraliFixed) continue;
      ac.__zoraliFixed = true;
      try { ac.update({ showOptionIcons: false, showChipIcons: false }); } catch (e) { /* older SDK */ }
      if (!userHasInteracted && typeof ac.blur === 'function') {
        try { ac.blur(); } catch (e) { /* ignore */ }
      }
    }
  }

  let userHasInteracted = false;
  const markInteraction = () => { userHasInteracted = true; };
  document.addEventListener('pointerdown', markInteraction, { capture: true, once: true });
  document.addEventListener('keydown', markInteraction, { capture: true, once: true });

  // ---- 2 + 4 + 5: styles injected into the widget's (open) shadow root --------------------
  const SHADOW_CSS = `
    .magicx-aia-option-icon,
    .magicx-aia-segment-icon { display: none !important; }
    .magicx-aia-option-content:has(.magicx-aia-option-icon) {
      padding-left: 0 !important;
      text-indent: 0 !important;
    }
    .magicx-aia-footer-brand-link { display: none !important; }
    .magicx-aia-product[${HIDDEN_ATTR}],
    .magicx-aia-option[${HIDDEN_ATTR}] { display: none !important; }
    .magicx-aia-products:not(:has(.magicx-aia-product:not([${HIDDEN_ATTR}]))) { display: none !important; }
    /* The theme's rotating label owns the empty, unfocused state; the SDK's own placeholder shows on focus. */
    :host(:not(:focus-within)) .magicx-aia-input[data-aia-empty="true"]::before { content: none !important; }
  `;

  function handleFromUrl(href) {
    if (!href) return '';
    const match = /\/products\/([^/?#]+)/.exec(href);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function priceFromText(text) {
    if (!text) return NaN;
    const match = /\d[\d,]*(?:\.\d+)?/.exec(text);
    return match ? parseFloat(match[0].replace(/,/g, '')) : NaN;
  }

  function isSparePart(card) {
    const handle = handleFromUrl(card.getAttribute('href'));
    const title = (card.querySelector('.magicx-aia-product-title') || {}).textContent || '';
    if (EXCLUDE_HANDLE.test(handle) || EXCLUDE_HANDLE.test(title.replace(/\s+/g, '-'))) return true;
    const price = priceFromText((card.querySelector('.magicx-aia-product-price') || {}).textContent);
    return Number.isFinite(price) && price < MIN_PRICE;
  }

  function hide(el) {
    el.setAttribute(HIDDEN_ATTR, '');
    el.setAttribute('aria-hidden', 'true');
    el.tabIndex = -1;
  }

  function filterProducts(root) {
    root.querySelectorAll('.magicx-aia-product').forEach((card) => {
      if (!card.hasAttribute(HIDDEN_ATTR) && isSparePart(card)) hide(card);
    });
    root.querySelectorAll('.magicx-aia-option').forEach((option) => {
      if (option.hasAttribute(HIDDEN_ATTR)) return;
      const text = ((option.querySelector('.magicx-aia-option-text') || option).textContent || '').trim();
      if (EXCLUDE_OPTION.test(text)) hide(option);
    });
  }

  // ---- 5: rotating label state -------------------------------------------------------------
  function updateIdle(host) {
    const frame = host.closest(FRAME_SELECTOR);
    if (!frame || !frame.querySelector('#label_tochange')) return;
    const root = host.shadowRoot;
    const input = root && root.querySelector('.magicx-aia-input');
    const empty = !input || input.getAttribute('data-aia-empty') !== 'false';
    const focused = frame.matches(':focus-within');
    frame.classList.toggle(IDLE_CLASS, empty && !focused);
  }

  const wiredHosts = new WeakSet();

  function wireHost(host) {
    const root = host.shadowRoot;
    if (!root) return;
    if (!root.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = SHADOW_CSS;
      root.appendChild(style);
    }
    filterProducts(root);
    updateIdle(host);
    if (wiredHosts.has(host)) return;
    wiredHosts.add(host);

    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        filterProducts(root);
        updateIdle(host);
      });
    };
    new MutationObserver(schedule).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-aia-empty', 'href']
    });
    // focusin/focusout are composed, so they cross the shadow boundary. Defer so :focus-within has settled.
    const frame = host.closest(FRAME_SELECTOR) || host;
    frame.addEventListener('focusin', () => setTimeout(() => updateIdle(host), 0));
    frame.addEventListener('focusout', () => setTimeout(() => updateIdle(host), 0));
  }

  let scanQueued = false;
  function scan() {
    scanQueued = false;
    fixExistingInstances();
    document.querySelectorAll(HOST_SELECTOR).forEach(wireHost);
  }
  function scheduleScan() {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(scan);
  }

  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType === 1 && (node.matches(HOST_SELECTOR) || node.querySelector(HOST_SELECTOR))) {
          scheduleScan();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('focusin', scheduleScan, true);
  document.addEventListener('shopify:section:load', scheduleScan);
  window.addEventListener('load', scheduleScan, { once: true });
  if (document.readyState !== 'loading') scheduleScan();
  else document.addEventListener('DOMContentLoaded', scheduleScan, { once: true });
})();
